// ui/presenters/chargeLineRowPresenter.js
import { fromMinorUnits, isChargeMinorUnits } from '../../domain/constants/currency';
import { SERVICE_CODE_ORIGIN } from '../../domain/constants/serviceCode';
import { isRetiredOn } from '../../domain/services/serviceCodeValidity';
import { toAsOfDate, toDateStr } from '../../domain/services/effectiveDating';

/**
 * A charge line and the code it references, as one row of the sheet.
 *
 * PURE, and takes `asOf` as an argument. Which lifecycle state a row is in depends entirely on the
 * date being asked about — a code is "expiring" until it is "expired" — so a presenter that read a
 * clock would disagree with the composable that opened the screen.
 *
 * THREE STATES PER ROW, NOT ONE, and keeping them apart is the point. Whether the clinic has set a
 * charge is the clinic's own business; what the code's lifecycle is doing is an industry fact that
 * reaches every clinic at once; and who authored the code is a fact about the record that never
 * changes at all. They vary independently — a future code can be charged, an expired one can have no
 * charge, an org's own code can be any of those — and a single combined state would have to
 * enumerate the product of all three and would still lose which part a screen is reacting to.
 *
 * PROVENANCE IS NOT A `CODE_LIFECYCLE` MEMBER, and that is the same argument one axis further. An
 * org's own code has a lifecycle like any other: it starts, it may expire, it may not have begun
 * yet. Folding "ours" into that enum would make those states mutually exclusive with it, so a row
 * could say "your own code" or "expiring" but never both — and the row that most needs to say both is
 * an org code whose funder is about to stop taking it.
 *
 * STATES, NOT ALARMS. Every value here describes what is true, and none of them means "wrong". A
 * code that expired in 2019 and is still on a clinic's sheet is not an error to be cleaned up: some
 * funders — workers' compensation, auto and liability, school districts, parts of state Medicaid —
 * accept or require the old code for years. The clinic removes it when its funders stop taking it,
 * and nothing here should nag them into doing it sooner.
 *
 * NO SHEET-LEVEL SCORE IS DERIVABLE FROM THESE ROWS, deliberately. A clinic charges for a small
 * subset of the catalog by design, so "N of M priced" has no denominator and any progress meter
 * built from one would be measuring the size of our catalog rather than the clinic's completeness.
 */

/**
 * Where a code sits in its own lifespan, on the date being asked about.
 *
 * `unknown` is the dangling-reference case: the sheet names a code the catalog does not hold. It
 * is shown rather than hidden, because a row that silently disappeared would take a charge somebody
 * entered with it, and the clinic would have no way to find out what happened.
 */
export const CODE_LIFECYCLE = {
  ACTIVE: 'active',
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
  FUTURE: 'future',
  UNKNOWN: 'unknown',
};

/**
 * @param {import('../../domain/model/ServiceCode.types').ServiceCode|null} serviceCode
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {string} a CODE_LIFECYCLE member
 */
export function codeLifecycleOn(serviceCode, asOf) {
  if (serviceCode === null || serviceCode === undefined) {
    return CODE_LIFECYCLE.UNKNOWN;
  }
  const validFrom = toDateStr(serviceCode.validFrom);
  const asOfDate = toAsOfDate(asOf);

  // A code with no readable start has no position in time. Calling it active would let one
  // mis-seeded record look ordinary on every screen that shows it.
  if (validFrom === null || asOfDate === null) {
    return CODE_LIFECYCLE.UNKNOWN;
  }
  if (isRetiredOn(serviceCode, asOf)) {
    return CODE_LIFECYCLE.EXPIRED;
  }
  if (validFrom > asOfDate) {
    return CODE_LIFECYCLE.FUTURE;
  }
  // Retired on a date that has not arrived: still fully billable, and worth saying so before it
  // stops being so. Informational, never a warning.
  return toDateStr(serviceCode.retiredOn) === null
    ? CODE_LIFECYCLE.ACTIVE
    : CODE_LIFECYCLE.EXPIRING;
} // codeLifecycleOn()

/**
 * One charge line as a row.
 *
 * THE UNIT BASIS IS READ OFF THE CODE, never off the line, and it is returned beside the charge
 * because a charge is not legible without it: "120.00" says nothing, "120.00 per 15 min" and
 * "120.00 per hour" differ fourfold. The line carries no copy of the basis, which is what makes it
 * impossible for the two to disagree.
 *
 * RETURNS TOKENS, NOT SENTENCES. `lifecycle` and the raw dates go back; turning them into "Retired
 * 31 Dec 2026" is the component's job, because that is translation and date formatting and neither
 * belongs in a presenter that has to stay testable without either.
 *
 * @param {import('../../domain/model/ChargeLine.types').ChargeLine} line
 * @param {import('../../domain/model/ServiceCode.types').ServiceCode|null} serviceCode - resolved
 *   through `line.serviceCodeId`; null when the catalog does not hold it
 * @param {{ currency: string, asOf: string }} context
 * @returns {Object}
 */
export function presentChargeLineRow(line, serviceCode, { currency, asOf }) {
  const chargeMinor = isChargeMinorUnits(line?.chargeMinor) ? line.chargeMinor : null;

  return {
    serviceCodeId: line?.serviceCodeId ?? null,
    // The code STRING for display. Null where the reference dangles — the row still renders, and the
    // id is what somebody would quote to support.
    code: serviceCode?.code ?? null,
    descriptor: serviceCode?.descriptor ?? '',
    unitBasis: serviceCode?.unitBasis ?? null,
    /** 'YYYY-MM-DD' or null — the component decides how to write a date. */
    retiredOn: toDateStr(serviceCode?.retiredOn),
    validFrom: toDateStr(serviceCode?.validFrom),
    lifecycle: codeLifecycleOn(serviceCode ?? null, asOf),
    /**
     * Did this clinic author the code? The third row axis, independent of the other two.
     *
     * FALSE FOR A DANGLING REFERENCE, not null. A row whose code the catalog does not hold shows
     * as an unknown code, and claiming it as the clinic's own would offer retire and remove actions
     * against a record that is not there.
     */
    isOwnCode: serviceCode?.origin === SERVICE_CODE_ORIGIN.CUSTOM,
    /**
     * Has the clinic closed its own code, whatever the date? Distinct from the `expired` lifecycle,
     * which is derived from `asOf` — a code retired for next January is not expired today, and the
     * row still has to say a retirement is booked.
     */
    hasRetirementBooked: toDateStr(serviceCode?.retiredOn) !== null,
    /** Minor units, for anything that needs to compute rather than display. */
    chargeMinor,
    /** The major-unit string a field shows, e.g. '120.50'. Null when no charge is set. */
    charge: fromMinorUnits(chargeMinor, currency),
    /**
     * Has the clinic set a charge? A zero charge counts — it says the clinic bills this code at no
     * cost, which is a decision, where an absent one says nobody has decided yet.
     */
    hasCharge: chargeMinor !== null,
    /**
     * EDITOR STATE, PASSED THROUGH — absent on a stored line and false everywhere but the editor.
     *
     * A line the user has marked stays on screen, struck through, so the sheet shows what the next
     * version will and will not contain while they are still deciding. It never reaches storage: the
     * editor drops marked lines before writing, which is how the model already says "this line
     * ended".
     */
    isPendingRemoval: line?.isPendingRemoval === true,
    /**
     * Where the line came from, which decides what removing it MEANS. A line off the committed sheet
     * is marked and restorable; one added in this session has no committed state to diff against, so
     * it is discarded outright with an undo. Null on a stored line, which has no editor history.
     */
    origin: line?.origin ?? null,
  };
} // presentChargeLineRow()

/**
 * Every line on a sheet as rows, in the order the sheet lists them.
 *
 * ORDER IS THE SHEET'S, not sorted here. The editor appends a picked code to the end so the row a
 * user just added is where they are looking; imposing an order in the presenter would make it jump
 * somewhere else the moment it appeared.
 *
 * @param {import('../../domain/model/ChargeMaster.types').ChargeMaster|null} chargeMaster
 * @param {(serviceCodeId: string) => (import('../../domain/model/ServiceCode.types').ServiceCode|null)}
 *   codeById - the catalog lookup, supplied by the caller that owns the loaded catalog
 * @param {{ asOf: string }} context
 * @returns {Object[]}
 */
export function presentChargeLineRows(chargeMaster, codeById, { asOf }) {
  const currency = chargeMaster?.currency ?? null;

  return (chargeMaster?.lines ?? []).map((line) => presentChargeLineRow(
    line,
    codeById(line?.serviceCodeId) ?? null,
    { currency, asOf },
  ));
} // presentChargeLineRows()
